import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { ArticleAddon, SubArticle } from "types/article";
import {
	ThemeBorderVariable,
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Checkbox } from "@caspeco/casper-ui-library.components.checkbox";
import { CheckboxGroup } from "@caspeco/casper-ui-library.components.checkbox-group";
import { Stack } from "@caspeco/casper-ui-library.components.stack";
import { Button } from "@caspeco/casper-ui-library.components.button";
import { Divider } from "@caspeco/casper-ui-library.components.divider";
import { Icons } from "@caspeco/casper-ui-library.components.icon";
import { FormControl, FormLabel } from "@caspeco/casper-ui-library.components.formcontrol";
import { SubArticleLabel } from "./SubArticleLabel";

interface IArticleModalExtras {
	extra: ArticleAddon;
	selectedExtras: string[];
	onChange: (nextValue: string, checked: boolean) => void;
	/**
	 * Article is not available for purchase at all.
	 * Used to indicate all extras are unavailable as well.
	 */
	isNotAvailableForPurchase: boolean;
}

const TRUNCATE_AT_ITEM_NUMBER = 5;

export function ArticleModalExtras({
	extra,
	onChange,
	selectedExtras,
	isNotAvailableForPurchase,
}: IArticleModalExtras) {
	const { t } = useTranslation();
	const { items } = extra;
	const numberOfItems: number = items.length;
	let initialValuesWouldBeTruncated = false;

	if (numberOfItems > TRUNCATE_AT_ITEM_NUMBER) {
		// The list of items to display should initially be truncated at (TRUNCATE_AT_ITEM_NUMBER)
		// However, if ANY of the initially selecteded items appears in order after (TRUNCATE_AT_ITEM_NUMBER)
		// all items must be shown by default
		const firstFiveItemIds = items.slice(0, TRUNCATE_AT_ITEM_NUMBER).map((item) => item.id);
		initialValuesWouldBeTruncated = !selectedExtras.every((id) => firstFiveItemIds.includes(id));
	}

	const [showAll, setShowAll] = useState<boolean>(initialValuesWouldBeTruncated);

	const onToggleShowAll = () => setShowAll(!showAll);
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value, e.target.checked);
	};

	const itemsToDisplay: SubArticle[] = showAll ? items : items.slice(0, TRUNCATE_AT_ITEM_NUMBER);

	const isGroupDisabled = isNotAvailableForPurchase || !items.some((item) => item.status === "ENABLED");

	if (numberOfItems === 0) return null;

	return (
		<Box>
			<FormControl as="fieldset" isDisabled={isGroupDisabled}>
				<FormLabel
					as="legend"
					fontFamily={ThemeFontVariable.SubHeader}
					color={ThemeColorVariable.OnSurfaceHeader}
					fontSize={{
						base: ThemeFontSizeVariable.Large,
						md: ThemeFontSizeVariable.Medium,
					}}
					mb={ThemeSpaceVariable.Small}
					fontWeight="medium"
				>
					{extra.name}
				</FormLabel>
				<CheckboxGroup defaultValue={selectedExtras} isDisabled={isGroupDisabled}>
					<Stack
						border={ThemeBorderVariable.XSmall}
						borderColor={ThemeColorVariable.OnSurfaceBorder}
						bg={isGroupDisabled ? ThemeColorVariable.SurfaceDisabled : undefined}
						borderRadius={ThemeRadiusVariable.Small}
						spacing={ThemeSpaceVariable.None}
						divider={<Divider borderColor={ThemeColorVariable.OnSurfaceBorder} />}
					>
						{itemsToDisplay.map((item) => {
							const isChecked = selectedExtras.includes(item.id);
							const isDisabled = isGroupDisabled || item.status !== "ENABLED";
							const backgroundColor = isDisabled ? ThemeColorVariable.SurfaceDisabled : undefined;

							return (
								<Checkbox
									id={item.id}
									key={item.id}
									value={item.id}
									isDisabled={isDisabled}
									variant="addon"
									size="lg"
									onChange={handleChange}
									px={ThemeSpaceVariable.Medium}
									py={ThemeSpaceVariable.Small}
									backgroundColor={backgroundColor}
									flexDirection="row-reverse"
									justifyContent="space-between"
									width="100%"
								>
									<SubArticleLabel item={item} isChecked={isChecked} isDisabled={isDisabled} />
								</Checkbox>
							);
						})}
					</Stack>
				</CheckboxGroup>
			</FormControl>
			{numberOfItems > TRUNCATE_AT_ITEM_NUMBER && !showAll && (
				<Box mt={ThemeSpaceVariable.Small}>
					<Button
						variant="ghost"
						onClick={onToggleShowAll}
						display="flex"
						gap={ThemeSpaceVariable.Small}
						justifyContent="space-around"
						rightIcon={Icons.Add}
					>
						<Text
							color={ThemeColorVariable.OnSurface}
							fontWeight="inherit"
							fontSize="inherit"
							fontFamily="inherit"
						>
							{t("action_show_more")}
						</Text>
					</Button>
				</Box>
			)}
		</Box>
	);
}
